import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, onSnapshot, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import AppLayout from '../components/AppLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Icons } from '../lib/Icons';
import { useSchoolId } from '../lib/auth/schoolContext';

const TARGET_SCORE_LABELS = {
  not_met: 'Not yet met',
  partial: 'Partially met',
  achieved: 'Achieved',
};

const TARGET_SCORE_BADGES = {
  not_met: 'bg-rose-50 text-rose-700 border-rose-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  achieved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function getTargetText(target) {
  if (!target) return '';
  if (typeof target === 'string') return target;
  return target.text || target.title || target.name || '';
}

function getTargetId(target) {
  if (!target) return '';
  if (typeof target === 'string') return target;
  return target.id || target.text || target.title || target.name || '';
}

function formatGeneratedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

const OnePageProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { schoolId } = useSchoolId();
  const [student, setStudent] = useState(null);
  const [onePageProfile, setOnePageProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interventionTargets, setInterventionTargets] = useState([]);
  const [loadingInterventionTargets, setLoadingInterventionTargets] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const studentSnap = await getDoc(doc(db, 'students', id));
        if (!studentSnap.exists()) {
          navigate('/student-organization');
          return;
        }
        if (!cancelled) {
          setStudent({ id: studentSnap.id, ...studentSnap.data() });
        }

        try {
          const profileRef = doc(db, 'v2_one_page_profiles', id);
          const profileSnap = await getDoc(profileRef);
          if (!cancelled) {
            setOnePageProfile(profileSnap.exists() ? profileSnap.data() : null);
          }
        } catch (err) {
          console.warn('Could not load one-page profile snapshot', err?.message);
          if (!cancelled) setOnePageProfile(null);
        }
      } catch (error) {
        console.error('Error loading one-page profile:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return undefined;
    const profileRef = doc(db, 'v2_one_page_profiles', id);
    return onSnapshot(
      profileRef,
      (snap) => setOnePageProfile(snap.exists() ? snap.data() : null),
      (err) => console.warn('One-page profile subscription error', err?.message)
    );
  }, [id]);

  useEffect(() => {
    if (!id || !schoolId) return;
    let cancelled = false;

    const loadInterventionTargets = async () => {
      try {
        setLoadingInterventionTargets(true);
        const snap = await getDocs(
          query(
            collection(db, 'interventions'),
            where('schoolId', '==', schoolId),
            where('students', 'array-contains', id)
          )
        );
        const interventions = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const enriched = await Promise.all(
          interventions.map(async (intervention) => {
            const targets = Array.isArray(intervention.targets) ? intervention.targets : [];
            if (targets.length === 0) {
              return { ...intervention, targetsWithScores: [] };
            }
            let latestScores = {};
            try {
              const sessionsSnap = await getDocs(
                query(
                  collection(db, 'sessionLogs'),
                  where('schoolId', '==', schoolId),
                  where('interventionId', '==', intervention.id),
                  orderBy('date', 'desc'),
                  limit(1)
                )
              );
              if (!sessionsSnap.empty) {
                const latest = sessionsSnap.docs[0].data();
                latestScores = latest?.targetScores?.[id] || {};
              }
            } catch (err) {
              console.warn('Could not fetch latest session log for intervention', intervention.id, err?.message);
            }
            return {
              ...intervention,
              targetsWithScores: targets
                .map((target) => ({
                  id: getTargetId(target),
                  text: getTargetText(target),
                  score: latestScores[getTargetId(target)] || null,
                }))
                .filter((t) => t.text),
            };
          })
        );
        if (!cancelled) {
          setInterventionTargets(enriched.filter((i) => i.targetsWithScores.length > 0));
        }
      } catch (err) {
        console.warn('Could not load interventions for one-page profile', err?.message);
        if (!cancelled) setInterventionTargets([]);
      } finally {
        if (!cancelled) setLoadingInterventionTargets(false);
      }
    };

    loadInterventionTargets();
    return () => {
      cancelled = true;
    };
  }, [id, schoolId]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading one-page profile...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900">Student not found</h1>
        </div>
      </AppLayout>
    );
  }

  const studentName = `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Student';
  const strategies = onePageProfile?.topStrategies?.length
    ? onePageProfile.topStrategies
    : [];
  const headlineNeeds = onePageProfile?.headlineNeeds || [];
  const doNotList = onePageProfile?.doNotList || [];
  const generatedLabel = formatGeneratedAt(onePageProfile?.generatedAt);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div>
          <p className="text-sm text-gray-500 mb-1">
            <Link to={`/student/${id}`} className="text-indigo-600 hover:underline">
              Back to Individual Support Plan
            </Link>
          </p>
          <h1 className="text-3xl font-bold">One-Page Profile: {studentName}</h1>
          {generatedLabel && (
            <p className="mt-2 text-xs text-gray-500">Last updated: {generatedLabel}</p>
          )}
        </div>

        {!onePageProfile && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            No profile data yet.
          </div>
        )}

        {(headlineNeeds.length > 0 || strategies.length > 0 || doNotList.length > 0) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Key needs and classroom strategies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {headlineNeeds.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Headline needs</h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-800">
                    {headlineNeeds.map((need) => (
                      <li key={need}>{need}</li>
                    ))}
                  </ul>
                </div>
              )}
              {strategies.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">What to do in lessons</h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-800">
                    {strategies.map((strategy) => (
                      <li key={strategy}>{strategy}</li>
                    ))}
                  </ul>
                </div>
              )}
              {doNotList.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Avoid</h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-800">
                    {doNotList.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-indigo-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Icons.Target className="h-5 w-5 text-indigo-600" />
              What I&apos;m working on in interventions
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Live from active interventions: reinforce these skills in lessons where you can.
            </p>
          </CardHeader>
          <CardContent>
            {loadingInterventionTargets ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : interventionTargets.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                No active interventions with SMART targets are currently logged for {student.firstName}.
              </p>
            ) : (
              <ul className="space-y-4">
                {interventionTargets.map((intervention) => (
                  <li key={intervention.id}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {intervention.name || intervention.interventionType}
                      </h4>
                      {intervention.frequency && (
                        <span className="text-xs text-gray-500">
                          {intervention.frequency}
                          {intervention.duration ? ` • ${intervention.duration}` : ''}
                        </span>
                      )}
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {intervention.targetsWithScores.map((target) => (
                        <li key={target.id} className="flex items-start gap-3 text-sm">
                          <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-indigo-400 flex-shrink-0" />
                          <span className="flex-1 text-gray-800">{target.text}</span>
                          {target.score && TARGET_SCORE_LABELS[target.score] && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border ${TARGET_SCORE_BADGES[target.score]}`}
                            >
                              {TARGET_SCORE_LABELS[target.score]}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {(student.pupilVoice?.whatHelpsMe || student.pupilVoice?.whatIFindHard || student.pupilVoice?.myGoals) && (
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Icons.Sparkles className="h-5 w-5 text-amber-600" />
                In {student.firstName}&apos;s own words
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {student.pupilVoice?.whatHelpsMe && (
                  <div className="p-3 rounded-md bg-amber-50/50 border border-amber-100">
                    <dt className="text-xs font-semibold text-amber-800">What helps me</dt>
                    <dd className="mt-1 text-gray-800 whitespace-pre-wrap">{student.pupilVoice.whatHelpsMe}</dd>
                  </div>
                )}
                {student.pupilVoice?.whatIFindHard && (
                  <div className="p-3 rounded-md bg-amber-50/50 border border-amber-100">
                    <dt className="text-xs font-semibold text-amber-800">What I find hard</dt>
                    <dd className="mt-1 text-gray-800 whitespace-pre-wrap">{student.pupilVoice.whatIFindHard}</dd>
                  </div>
                )}
                {student.pupilVoice?.myGoals && (
                  <div className="p-3 rounded-md bg-amber-50/50 border border-amber-100">
                    <dt className="text-xs font-semibold text-amber-800">My goals</dt>
                    <dd className="mt-1 text-gray-800 whitespace-pre-wrap">{student.pupilVoice.myGoals}</dd>
                  </div>
                )}
              </dl>
              <p className="mt-3 text-xs text-gray-500">
                Live from the Individual Support Plan.{' '}
                <Link to={`/student/${id}?tab=overview`} className="text-indigo-600 hover:underline">
                  Update voice
                </Link>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};

export default OnePageProfilePage;
